import React, {useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {Button, Dialog, Image, NavBar, Skeleton, Toast} from 'antd-mobile';
import {getNavBarContainerStyle, getBottomNavBarContainerStyle} from "../../../utils/deviceUtils";
import {useLazyQuery, useMutation} from '@apollo/client';

import PhotoUploadCard from '../components/PhotoUploadCard';
import PhotoViewer from '../components/PhotoViewer';
import MultiPhotoSelectorView from '../components/MultiPhotoSelectorView';
import InfoItem from '../components/InfoItem';
import photoService, {PhotoType, PositionType} from '../services/PhotoService';
import multiPhotoService from '../services/MultiPhotoService';
import {NativePermissions} from '../components/PhotoNativeBridge';
import DeliveryDataService, {FailedDeliveryOrder} from "../../../services/DeliveryDataService";
import {PhotoUploadManager} from "../utils/PhotoUploadManager";
import {PhotoProcessingUtils} from "../utils/PhotoProcessingUtils";
import EmptyState from "../../../components/EmptyState";
import {timestampToDate} from "../../../utils/dateTimeUtils";
import {formatLocalizedDateTime} from "../../../utils/dateFormatUtils";
import {useDeliveryTaskOrder, useDeliveryTaskOrderOperations, useFromMapMode, useDeliveryMethodI18nConfig} from '../store/deliveryStore';
import {useLocationStore} from "../../../store/locationStore";
import {navigateBack} from "../../../components/Map/MapNavigationUtils";
import i18n, {t} from "../../../i18n";
import {
    GET_DELIVERY_TASK_ORDER_BY_ID,
    GetDeliveryTaskOrderByIdResponse
} from "../graphql/queries/getDeliveryTaskOrderById";
import {DeliveryTaskOrderType} from "../graphql/types/DeliveryTaskOrderType";
import {isAppVersionGreaterThanOrEqual, kNativeMultiImagePickerVersion, kNativeMapModeVersion} from "../../../utils/versionUtils";
import {openCamera, openNativeMapMode} from "../../../utils/appRuntime";
import MultiPhotoGalleryView from '../components/MultiPhotoGalleryView';
import { formatStopNumberFromDeliveryTaskOrder } from '../../../utils/stopNumberUtils';
import geolocationService, { GeoPosition } from "../services/GeolocationService";
import { getUserInfo, isDspManager } from '../../../utils/auth';
import { PodReviewStatus } from '../../../utils/podStatusUtils';
import OrderNoteItem from '../../../components/OrderNoteItem/OrderNoteItem';
import {maskPhoneNumber} from "../../../utils/phoneUtils";
import { getTranslatedPodReviewMessage } from "../../../utils/podReviewMessageUtils";
import DeliveryMethodDisplayCard from '../components/DeliveryMethodDisplayCard';


const kMinSelectorPhotoLength = 3
const kMaxSelectorPhotoLength = 6

// 编辑状态枚举
enum EditState {
  EDITABLE = 'editable',           // 图片可编辑，按钮可点击
  DISABLED = 'disabled',           // 图片不可编辑，按钮灰色禁用
  HIDDEN = 'hidden'               // 图片不可编辑，按钮隐藏
}

interface DeliveredDetailProps {}
// 定义已知的POD错误类型
const KNOWN_ERROR_TYPES = ['NO_LABEL_PHOTO', 'WRONG_TRK', 'NO_HOUSE_APT', 'WRONG_HOUSE_APT'];

const DeliveredDetailPage: React.FC<DeliveredDetailProps> = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [photos, setPhotos] = useState<Record<PositionType, PhotoType | undefined>>({} as Record<PositionType, PhotoType | undefined>);
    const [photosArray, setPhotosArray] = useState<PhotoType[]>([]);
    const [viewingPhoto, setViewingPhoto] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showMultiPhotoSelector, setShowMultiPhotoSelector] = useState<boolean>(false);
    // 是否已展开，控制提示信息...的展开和收起
    const [isOtherMessagesExpanded, setIsOtherMessagesExpanded] = useState<boolean>(false);
    const [isVersionGte820, setIsVersionGte820] = useState<boolean>(false);
    const deliveryTaskOrderId = location.state?.deliveryTaskOrderId;
    const trackingNumber = location.state?.trackingNumber || '';
    const {deliveryTaskOrder: localDeliveryTaskOrder,podTaskOrder, loading: localLoading} = useDeliveryTaskOrder(deliveryTaskOrderId);
    const deliveryMethodI18nConfig = useDeliveryMethodI18nConfig();
    const [remoteDeliveryTaskOrder, setRemoteDeliveryTaskOrder] = useState<DeliveryTaskOrderType | null>(null);
    const {handleDelivered} = useDeliveryTaskOrderOperations();
    const { position } = useLocationStore();
    const { fromMapMode, setFromMapMode } = useFromMapMode();
    const detailType = location.state?.detailType || 'detail';
    const fromNativeMapMode = location.state?.fromNativeMapMode || false;
    const fromDeliveringTemporary = location.state?.fromDeliveringTemporary === true;
    const userInfo = getUserInfo();
    sessionStorage.setItem('returningFromDetail', 'true');

    // 使用 useLazyQuery 进行懒加载查询
    const [getDeliveryTaskOrderById, { loading: remoteLoading }] = useLazyQuery<GetDeliveryTaskOrderByIdResponse>(
        GET_DELIVERY_TASK_ORDER_BY_ID,
        {
            fetchPolicy: 'cache-and-network',
            onCompleted: (data) => {
                if (data?.getDeliveryTaskOrderById) {
                    setRemoteDeliveryTaskOrder(data.getDeliveryTaskOrderById);
                }
            },
            onError: (error) => {
                console.error('Failed to fetch remote delivery task order:', error);
            }
        }
    );

    // 先从缓存中获取，无数据则查询数据库
    const deliveryTaskOrder = localDeliveryTaskOrder || remoteDeliveryTaskOrder;
    console.log("localDeliveryTaskOrder:", localDeliveryTaskOrder)
    console.log("remoteDeliveryTaskOrder:", remoteDeliveryTaskOrder)
    const loading = localLoading || remoteLoading;

    // 检查是否来自地图模式（Web地图）
    const isFromMapMode = fromMapMode || location.state?.fromMapMode;
    // 检查是否来自原生地图模式
    const isFromNativeMapMode = fromNativeMapMode;

    // 简化逻辑：如果detailType为detail直接调用远程接口，否则按原逻辑判断
    useEffect(() => {
        if (detailType === 'detail' && deliveryTaskOrderId && !localLoading) {
            getDeliveryTaskOrderById({
                variables: { deliveryTaskOrderId }
            });
        }

        if (detailType === 'pod' && deliveryTaskOrderId && !localLoading) {
            setRemoteDeliveryTaskOrder(podTaskOrder || null);
        }

    }, [localDeliveryTaskOrder, deliveryTaskOrderId, localLoading, getDeliveryTaskOrderById, detailType]);

    useEffect(() => {
        isAppVersionGreaterThanOrEqual(kNativeMapModeVersion).then(setIsVersionGte820);
    }, []);

    useEffect(() => {
        console.log('=== 详细调试 ===');
        console.log('trackingNumber:', trackingNumber);
        console.log('detailType:', detailType);
        console.log('localLoading:', localLoading);
        console.log('remoteLoading:', remoteLoading);
        console.log('localDeliveryTaskOrder:', localDeliveryTaskOrder);
        console.log('remoteDeliveryTaskOrder:', remoteDeliveryTaskOrder);
        console.log('final deliveryTaskOrder:', deliveryTaskOrder);
        
        if (deliveryTaskOrder) {
            const order = deliveryTaskOrder.orderWithAddress?.order;
            console.log('isAbnormal:', order?.isAbnormal);
            console.log('podImageS3UrlsJson length:', order?.podImageS3UrlsJson?.length);
            console.log('podImageS3KeysJson length:', order?.podImageS3KeysJson?.length);
            console.log('podImageS3UrlsJson:', order?.podImageS3UrlsJson);
            console.log('podImageS3KeysJson:', order?.podImageS3KeysJson);
        }
        
        // 输出照片的经纬度信息
        console.log('=== 照片经纬度信息 ===');
        console.log('photosArray:', photosArray.map(photo => ({
            url: photo.url.substring(0, 50) + '...',
            latitude: photo.latitude,
            longitude: photo.longitude,
            capturedAt: photo.capturedAt ? new Date(photo.capturedAt).toLocaleString() : undefined
        })));
        console.log('photos:', Object.entries(photos).map(([key, photo]) => ({
            type: key,
            url: photo?.url?.substring(0, 50) + '...',
            latitude: photo?.latitude,
            longitude: photo?.longitude,
            capturedAt: photo?.capturedAt ? new Date(photo.capturedAt).toLocaleString() : undefined
        })));

        if (deliveryTaskOrder) {
            const podImages = deliveryTaskOrder.orderWithAddress?.order?.podImageS3UrlsJson || [];
            const podImageKeys = deliveryTaskOrder?.orderWithAddress?.order?.podImageS3KeysJson || [];
            
            if (showMultiPhotoSelector) {
                // 新版本：数组形式
                console.log('podImages:', podImages);
                console.log('podImageKeys:', podImageKeys);
                if (podImages.length > 0 && podImageKeys.length > 0) {
                    setPhotosArray(podImages.map((url, index) => ({
                        url,
                        objectKey: podImageKeys[index] || ''
                    })));
                }
            } else {
                // 老版本：固定位置
                if (podImages.length > 0 && podImageKeys.length > 0) {
                    setPhotos({
                        label: {
                            url: podImages[0],
                            objectKey: podImageKeys[0]
                        },
                        location: {
                            url: podImages[1],
                            objectKey: podImageKeys[1]
                        },
                        houseNumber: {
                            url: podImages[2],
                            objectKey: podImageKeys[2]
                        },
                    });
                }
            }
        }
    }, [deliveryTaskOrder, showMultiPhotoSelector]);

    const deliveredAt = deliveryTaskOrder?.orderWithAddress?.order?.deliveredAt;
    const sixHoursLater = deliveredAt ? Number(deliveredAt) + 24 * 60 * 60 : null;
    const isWithinSixHours = sixHoursLater ? timestampToDate(sixHoursLater) > timestampToDate(Date.now()) : false;
    const podReviewStatus = deliveryTaskOrder?.orderWithAddress?.order?.podReviewStatus;
    const podHasBeenManualDisapproved = deliveryTaskOrder?.orderWithAddress?.order?.podHasBeenManualDisapproved;
    const isModifiedPodPhotos = deliveryTaskOrder?.orderWithAddress?.order?.isModifiedPodPhotos;
    const podReviewMessage = deliveryTaskOrder?.orderWithAddress?.order?.podReviewMessage;
    // 计算编辑状态的函数
    const getEditState = (): { state: EditState; warningMessage?: string } => {
        const isManager = isDspManager();

        // 场景1：DSP经理
        if (isManager) {
            if (isWithinSixHours) {
                return { state: EditState.EDITABLE };
            } else {
                return { state: EditState.HIDDEN };
            }
        }

        // 场景2：司机从异常页点击跳转，或者从已完成列表中点击跳转
        if (detailType === 'pod' || detailType === 'detail') {
            // 情况一：超过6小时
            if (!isWithinSixHours) {
                return { state: EditState.HIDDEN };
            }

            // 情况二：已到达修改次数上限
            if (podHasBeenManualDisapproved && isModifiedPodPhotos) {
                // 情况三：人审通过
                if (podReviewStatus === PodReviewStatus.MANUAL_APPROVED) {
                    return { state: EditState.HIDDEN };
                }
                return {
                    state: EditState.DISABLED,
                    warningMessage: t('pages.delivery.detail.maxModificationsReached')
                };
            }

            return { state: EditState.EDITABLE };
        }

        // 场景3：基础逻辑（非DSP经理，非pod类型）
        const edit = (detailType === 'delivered' || isWithinSixHours);
        if (edit) {
            return { state: EditState.EDITABLE };
        } else {
            return { state: EditState.HIDDEN };
        }
    };

    // 获取编辑状态结果
    const editStateResult = getEditState();
    const editState = editStateResult.state;

    // 判断图片是否可以编辑
    // true: 图片可编辑，用户可以拍照、删除照片
    // false: 图片不可编辑，只能查看
    const canEdit = editState === EditState.EDITABLE;

    // 判断提交按钮是否显示
    // true: 显示"完成"按钮
    // false: 隐藏按钮（HIDDEN 状态时）
    const showButton = editState !== EditState.HIDDEN;

    // 判断提交按钮是否禁用（灰色不可点击）
    // true: 按钮禁用，显示为灰色
    // false: 按钮可点击
    // 禁用条件：
    // 1. 编辑状态为 DISABLED（例如：已到达修改次数上限）
    // 2. 或者照片验证不通过（照片数量不足、照片未上传等）
    const buttonDisabled = editState === EditState.DISABLED || (
        showMultiPhotoSelector
            ? (photosArray.length < kMinSelectorPhotoLength || isLoading)
            : (!photos.label?.url || !photos.location?.url || !photos.houseNumber?.url || isLoading)
    );

    // 警告提示信息
    // 当编辑状态为 DISABLED 时，会显示警告信息
    // 例如："已到达修改次数上限，请联系DSP进行提交"
    // 其他状态时为 undefined，不显示警告
    const warningMessage = editStateResult.warningMessage;

    useEffect(() => {
        const requestPermissions = async () => {
            try {
                await NativePermissions.request('camera');
                await NativePermissions.request('storage');
            } catch (error) {
                console.error('Failed to request permissions:', error);
            }
        };

        requestPermissions();
    }, []);

    // 版本检查
    useEffect(() => {
        const checkVersion = async () => {
             const isHigher = await isAppVersionGreaterThanOrEqual(kNativeMultiImagePickerVersion);
             setShowMultiPhotoSelector(isHigher);
        };

        checkVersion();
    }, []);

    // 保存订单数据到本地
    const saveOrderToLocal = async (
        customPhotosArray?: PhotoType[],
        customPhotos?: Record<PositionType, PhotoType | undefined>,
        customCompletionLocation?: GeoPosition | null
    ): Promise<boolean> => {
        try {
            // 使用传入的参数或当前状态
            const currentPhotosArray = customPhotosArray || photosArray;
            const currentPhotos = customPhotos || photos;

            // 创建订单对象
            const order: FailedDeliveryOrder = {
                trackingNumber: trackingNumber,
                updatedAt: Date.now(),
                photos: PhotoProcessingUtils.processPhotosForSaving(currentPhotosArray, currentPhotos, showMultiPhotoSelector),
                location: position,
                stopNumber: deliveryTaskOrder?.stopNumber?.toString() || '0',
                subStopNumber: deliveryTaskOrder?.subStopNumber,
                id: deliveryTaskOrderId,
                type: 'delivered',
                // ✅ 新增字段：保存完成时GPS
                completionLocation: customCompletionLocation
            };

            // 保存到本地
            const saved = await DeliveryDataService.saveDeliveryOrder(order);
            return saved;
        } catch (error) {
            console.error('Failed to save order to local:', error);
            return false;
        }
    };

    /*照片相关操作 start======*/

    // 拍照或从相册选择 - 老版本
    const takePhoto = async (type: PositionType, file: File, source: 'camera' | 'gallery') => {
        try {
            setIsLoading(true);
            let currentPosition: GeoPosition | null = {
                latitude: 0,
                longitude: 0,
                accuracy: 0,
                timestamp: 0
            };

            await photoService.handlePhotoUnUpload(type, file, (updateFn) => {
                setPhotos((prev) => updateFn(prev));
            }, currentPosition, source);
        } catch (error) {
            console.error('Failed to take photo:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // 删除照片 - 老版本
    const deletePhoto = (type: PositionType) => {
        Dialog.confirm({
            content: t('pages.delivery.detail.deletePhotoTitle'),
            confirmText: t('pages.delivery.detail.deletePhotoDelete'),
            cancelText: t('pages.delivery.detail.deletePhotoCancel'),
            onConfirm: () => {
                photoService.handleDeletePhoto(type, (updateFn) => {
                    setPhotos((prev) => updateFn(prev));
                });
            },
        });
    };

    // 删除照片 - 新版本（数组）
    const deletePhotoArray = (index: number) => {
        Dialog.confirm({
            content: t('pages.delivery.detail.deletePhotoTitle'),
            confirmText: t('pages.delivery.detail.deletePhotoDelete'),
            cancelText: t('pages.delivery.detail.deletePhotoCancel'),
            onConfirm: () => {
                multiPhotoService.handleDeletePhotoArray(index, (updateFn) => {
                    setPhotosArray((prev) => updateFn(prev));
                });
            },
        });
    };

    // 查看大图
    const handleViewPhoto = (url: string) => setViewingPhoto(url);

    /*照片相关操作 end======*/

    // 处理多图画廊的连拍功能（新版本数组形式）
    const handleMultiPhotoGalleryBurstCapture = () => {
        // 计算剩余可拍照片数量
        const currentPhotoCount = photosArray.length;
        const remainingPhotoCount = Math.max(0, kMaxSelectorPhotoLength - currentPhotoCount);
        
        // 动态计算最小选择数量：如果当前照片数已经超过最小要求，则最小选择为0
        const minSelectionCount = currentPhotoCount >= kMinSelectorPhotoLength ? 0 : kMinSelectorPhotoLength - currentPhotoCount;
        
        openCamera(remainingPhotoCount, minSelectionCount, t('pages.delivery.detail.requiredPhotos'), async (photos: string[]) => {
            try {
                // ✅ 连拍时为每张照片获取实时位置（每张照片一个经纬度）
                const photosWithPosition: Array<{ photo: string; position: GeoPosition | null }> = [];

                for (const photo of photos) {
                    let currentPosition: GeoPosition | null = {
                        latitude: 0,
                        longitude: 0,
                        accuracy: 0,
                        timestamp: 0,
                    };
                    photosWithPosition.push({ photo, position: currentPosition });
                }

                await multiPhotoService.handleMultiPhotoSelectionArrayWithIndividualPositionsBatched(
                    photosWithPosition,
                    (updateFn) => setPhotosArray(updateFn),
                    'camera' // 连拍来源：拍照
                );
            } catch (error) {
                console.error('多图片处理失败:', error);
                Toast.show({ content: '多图片处理失败', position: 'center' });
            }
        });
    };

    // 完成派送
    const handleCompleteDelivery = async (): Promise<void> => {
        setIsLoading(true);

        // 创建照片上传管理器
        const photoUploadManager = new PhotoUploadManager(
            showMultiPhotoSelector,
            trackingNumber,
            setPhotosArray,
            setPhotos
        );

        // ✅ 获取完成时的实时经纬度（用于 location 字段）
        let completionPosition = position; // 默认使用缓存位置

        try {
            // 验证照片
            if (showMultiPhotoSelector) {
                // 新版本：至少需要3张图片，有url即可（按照老版本逻辑，objectKey在点击完成前都是FILE_UPLOAD_FAILED_KEY）
                if (photosArray.length < kMinSelectorPhotoLength || !photosArray.every(photo => photo.url)) {
                    Toast.show({
                        content: t('pages.delivery.detail.takeAtLeastPhotos', { count: kMinSelectorPhotoLength }),
                        position: 'center'
                    });
                    return;
                }
            } else {
                // 老版本：需要3张固定位置的图片
                const hasLabel = photos.label?.objectKey;
                const hasLocation = photos.location?.objectKey;
                const hasHouseNumber = photos.houseNumber?.objectKey;
                if (!hasLabel || !hasLocation || !hasHouseNumber) {
                    Toast.show({
                        content: t('pages.delivery.detail.takePhotosOfRequired'),
                        position: 'center'
                    });
                    return;
                }
            }

            // 获取当前照片状态
            const currentPhotos = showMultiPhotoSelector ? [...photosArray] : {...photos};
            
            // 格式化经纬度，保留7位小数（精度约1.1厘米）
            const formatCoordinate = (coord: number): number => {
                if (coord === 0) {
                    return 0;
                }
                return parseFloat(coord.toFixed(7));
            };

            // 准备照片信息（按来源分组）
            // 准备初始照片信息（乐观提交用，使用 objectKey）
            const preparePodImagesInfo = () => {
                const cameraPhotos: Array<{s3Key: string, latitude: number, longitude: number}> = [];
                let firstGalleryPhotoGPS: {latitude: number, longitude: number} | null = null;
                let hasGalleryPhotos = false;

                if (showMultiPhotoSelector) {
                    // 新版本：数组形式
                    photosArray.forEach(photo => {
                        if (!photo.objectKey) return;

                        if (photo.source === 'camera') {
                            cameraPhotos.push({
                                s3Key: photo.objectKey,
                                latitude: formatCoordinate(photo.latitude || 0),
                                longitude: formatCoordinate(photo.longitude || 0)
                            });
                        } else if (photo.source === 'gallery') {
                            // ✅ 记录第一张相册照片的GPS（照片自己保存的GPS）
                            if (!firstGalleryPhotoGPS) {
                                firstGalleryPhotoGPS = {
                                    latitude: formatCoordinate(photo.latitude || 0),
                                    longitude: formatCoordinate(photo.longitude || 0)
                                };
                            }
                            hasGalleryPhotos = true;
                        } else {
                            // 如果没有 source，默认为拍照
                            cameraPhotos.push({
                                s3Key: photo.objectKey,
                                latitude: formatCoordinate(photo.latitude || 0),
                                longitude: formatCoordinate(photo.longitude || 0)
                            });
                        }
                    });
                } else {
                    // 老版本：固定位置
                    const photoList = [photos.label, photos.location, photos.houseNumber];
                    photoList.forEach(photo => {
                        if (!photo?.objectKey) return;

                        if (photo.source === 'camera') {
                            cameraPhotos.push({
                                s3Key: photo.objectKey,
                                latitude: formatCoordinate(photo.latitude || 0),
                                longitude: formatCoordinate(photo.longitude || 0)
                            });
                        } else if (photo.source === 'gallery') {
                            // ✅ 记录第一张相册照片的GPS（照片自己保存的GPS）
                            if (!firstGalleryPhotoGPS) {
                                firstGalleryPhotoGPS = {
                                    latitude: formatCoordinate(photo.latitude || 0),
                                    longitude: formatCoordinate(photo.longitude || 0)
                                };
                            }
                            hasGalleryPhotos = true;
                        } else {
                            // 如果没有 source，默认为拍照
                            cameraPhotos.push({
                                s3Key: photo.objectKey,
                                latitude: formatCoordinate(photo.latitude || 0),
                                longitude: formatCoordinate(photo.longitude || 0)
                            });
                        }
                    });
                }

                return {
                    cameraPhotos,
                    galleryPhotos: (hasGalleryPhotos && firstGalleryPhotoGPS) ? firstGalleryPhotoGPS : null
                };
            };

            const initialPodImagesInfo = preparePodImagesInfo();

            // ✅ 准备 S3 keys 数组（兼容旧版本接口）
            const initialObjectKeys = showMultiPhotoSelector 
                ? photosArray.map(photo => photo.objectKey).filter((key): key is string => Boolean(key))
                : [photos.label?.objectKey, photos.location?.objectKey, photos.houseNumber?.objectKey].filter((key): key is string => Boolean(key));

            // 获取完成时的实时经纬度（强制获取最新位置，不使用缓存）
            try {
                completionPosition = await geolocationService.getFreshPosition();
                console.log('📍 完成派送时获取到最新位置:', completionPosition);
            } catch (error) {
                console.warn('⚠️ 获取位置失败，使用缓存位置:', error);
            }

            //仅执行乐观提交逻辑，不进行网络请求
            await handleDelivered({
                trackingNumber: trackingNumber,
                location: {
                    latitude: completionPosition?.latitude || 0.0,
                    longitude: completionPosition?.longitude || 0.0
                },
                deliveredAt: new Date().toISOString(),
                podImageS3KeysJson: initialObjectKeys,
                cameraPhotos: initialPodImagesInfo.cameraPhotos,
                uploadPhotos: initialPodImagesInfo.galleryPhotos,
                id: deliveryTaskOrderId
            }, true);
            
            // 恢复照片状态
            if (showMultiPhotoSelector) {
                setPhotosArray(currentPhotos as PhotoType[]);
            } else {
                setPhotos(currentPhotos as Record<PositionType, PhotoType | undefined>);
            }

            // 初始化照片数据并获取需要上传的照片
            photoUploadManager.initializeLocalCopies(photosArray, photos);
            const photosToUpload = photoUploadManager.getPhotosToUpload(photosArray, photos);

            // 执行照片上传
            const photoKeys = await photoUploadManager.uploadPhotos(photosToUpload);

            // 准备最新的照片信息（上传后，使用真实的 S3 keys）
            const prepareFinalPodImagesInfo = () => {
                const cameraPhotos: Array<{s3Key: string, latitude: number, longitude: number}> = [];
                let firstGalleryPhotoGPS: {latitude: number, longitude: number} | null = null;
                let hasGalleryPhotos = false;

                // ✅ 直接使用 photosToUpload 和 photoKeys 的顺序对应关系
                // photosToUpload 和 photoKeys 的索引是严格一一对应的
                photosToUpload.forEach((photo, index) => {
                    const s3Key = photoKeys[index];
                    if (!s3Key) return;

                    if (photo.source === 'camera') {
                        cameraPhotos.push({
                            s3Key: s3Key,
                            latitude: formatCoordinate(photo.latitude || 0),
                            longitude: formatCoordinate(photo.longitude || 0)
                        });
                    } else if (photo.source === 'gallery') {
                        // ✅ 记录第一张相册照片的GPS（照片自己保存的GPS）
                        if (!firstGalleryPhotoGPS) {
                            firstGalleryPhotoGPS = {
                                latitude: formatCoordinate(photo.latitude || 0),
                                longitude: formatCoordinate(photo.longitude || 0)
                            };
                        }
                        hasGalleryPhotos = true;
                    } else {
                        // 如果没有 source，默认为拍照
                        cameraPhotos.push({
                            s3Key: s3Key,
                            latitude: formatCoordinate(photo.latitude || 0),
                            longitude: formatCoordinate(photo.longitude || 0)
                        });
                    }
                });

                return {
                    cameraPhotos,
                    galleryPhotos: (hasGalleryPhotos && firstGalleryPhotoGPS) ? firstGalleryPhotoGPS : null
                };
            };

            const finalPodImagesInfo = prepareFinalPodImagesInfo();

            //真正的调用接口（使用完成时获取的经纬度）
            await handleDelivered({
                trackingNumber: trackingNumber,
                location: {
                    latitude: completionPosition?.latitude || 0.0,
                    longitude: completionPosition?.longitude || 0.0
                },
                deliveredAt: new Date().toISOString(),
                podImageS3KeysJson: photoKeys, // ✅ 保留旧字段
                cameraPhotos: finalPodImagesInfo.cameraPhotos, // ✅ 新增字段
                uploadPhotos: finalPodImagesInfo.galleryPhotos, // ✅ 新增字段
                id: deliveryTaskOrderId
            }, false);

            // 根据导航来源决定返回路径
            const source = location.state?.source;
            if (isFromNativeMapMode) {
                // 从原生地图跳转过来，返回后需要重新打开原生地图
                openNativeMapMode();
                navigateBack.navigateToDeliveringList(navigate);
            } else if (isFromMapMode) {
                setFromMapMode(false); // 清除导航来源
                // 使用兼容的导航方式
                sessionStorage.setItem('returningFromDetail', 'true');
                sessionStorage.setItem('pickUpSuccess', 'true');
                navigateBack.navigateToLsatPage(navigate);
            } else if (source === 'driver-violation-review') {
                // 从DSP司机违规审核页面跳转来的，返回到该页面
                navigate('/dsp/scan/driver-violation-review');
            } else {
                fromDeliveringTemporary ? navigate('/delivery/delivering/temporary') : navigateBack.navigateToDeliveringList(navigate);
            }

            if (!isFromNativeMapMode) {
                // 根据派送状态显示提示
                if (isDelivered) {
                    Toast.show({ content: t('pages.delivery.detail.modificationSuccessful'), position: 'center' });
                } else {
                    Toast.show({ content: t('pages.delivery.detail.deliveryCompleted'), position: 'center' });
                }
            }
        } catch (error: any) {
            if (error.networkError) {
                // ✅ 获取最新的照片数据并保存到本地缓存（照片本身包含GPS信息）
                const { photosArray: latestPhotosArray, photos: latestPhotos } = photoUploadManager.getLatestPhotosData();

                // ✅ 直接保存照片和完成时GPS，照片的经纬度会在重试时从照片本身提取
                await saveOrderToLocal(
                    latestPhotosArray,
                    latestPhotos,
                    completionPosition // 保存完成时的GPS
                );
                const source = location.state?.source;

                if (isFromNativeMapMode) {
                    // 从原生地图跳转过来，返回后需要重新打开原生地图
                    openNativeMapMode();
                    navigateBack.navigateToDeliveringList(navigate);
                } else if (isFromMapMode) {
                    setFromMapMode(false);
                    // 使用兼容的导航方式
                    sessionStorage.setItem('returningFromDetail', 'true');
                    sessionStorage.setItem('pickUpSuccess', 'true');
                    navigateBack.navigateToLsatPage(navigate);
                } else if (source === 'driver-violation-review') {
                    // 从DSP司机违规审核页面跳转来的，返回到该页面
                    navigate('/dsp/scan/driver-violation-review');
                } else {
                    fromDeliveringTemporary ? navigate('/delivery/delivering/temporary') : navigateBack.navigateToDeliveringList(navigate);
                }
            } else {
                console.error('Failed to complete delivery:', error);
                Toast.show({content: t('pages.delivery.detail.deliveryFailed'), position: 'center'});
            }
        } finally {
            setIsLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col h-screen">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={() => {
                        if (isFromNativeMapMode) {
                            openNativeMapMode();
                            navigateBack.navigateToDeliveringList(navigate);
                        } else {
                            navigate(-1);
                        }
                    }}>
                        {trackingNumber}
                    </NavBar>
                </div>
                <Skeleton.Title animated/>
                <Skeleton.Paragraph lineCount={5} animated/>
                <Skeleton.Title animated/>
                <Skeleton.Paragraph lineCount={3} animated/>
                <Skeleton.Title animated/>
                <Skeleton.Paragraph lineCount={1} animated/>
                <Skeleton style={{width: '100%', height: '30%'}} animated/>
            </div>
        );
    }

    if (!deliveryTaskOrder) {
        return (
            <div className="flex flex-col h-screen">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={() => {
                        if (isFromNativeMapMode) {
                            openNativeMapMode()
                            navigateBack.navigateToDeliveringList(navigate);
                        } else {
                            navigate(-1);
                        }
                    }}>
                        {trackingNumber}
                    </NavBar>
                </div>
                <EmptyState/>
            </div>
        );
    }

    const address = location.state?.displayAddress || [
        deliveryTaskOrder.orderWithAddress?.deliveryAddress?.addressLine1 || '',
        deliveryTaskOrder.orderWithAddress?.deliveryAddress?.addressLine2 || '',
        deliveryTaskOrder.orderWithAddress?.deliveryAddress?.city || '',
        deliveryTaskOrder.orderWithAddress?.deliveryAddress?.state || '',
        deliveryTaskOrder.orderWithAddress?.deliveryAddress?.zipcode || '',
        'US'
    ].filter(Boolean).join(' ');
    const stopNumber = deliveryTaskOrder.stopNumber;
    const fullName = deliveryTaskOrder.orderWithAddress?.deliveryAddress?.fullName;
    const phone = deliveryTaskOrder.orderWithAddress?.deliveryAddress?.phoneNumber;
    const extension = deliveryTaskOrder.orderWithAddress?.deliveryAddress?.extension;
    const routeNumber = deliveryTaskOrder.routeName;
    const deliverAt = deliveryTaskOrder.orderWithAddress?.order?.deliverAt;
    const deliveryAttempts = deliveryTaskOrder.orderWithAddress?.order?.deliveryAttempts || 0;
    const isDelivered =deliveryTaskOrder.orderWithAddress?.order.isDelivered
    // 图片多选 
    const multiPhotoSelectorView = (() => {
        // 计算剩余可拍照片数量
        const currentPhotoCount = photosArray.length;
        const remainingPhotoCount = Math.max(0, kMaxSelectorPhotoLength - currentPhotoCount);
        
        // 动态计算最小选择数量：如果当前照片数已经超过最小要求，则最小选择为0
        const minSelectionCount = currentPhotoCount >= kMinSelectorPhotoLength ? 0 : kMinSelectorPhotoLength - currentPhotoCount;
        
        return (
            <MultiPhotoSelectorView 
                minPhotos={minSelectionCount} 
                maxPhotos={remainingPhotoCount} 
                onPhotoSelected={async (photos: string[]) => {
                    try {
                        let currentPosition: GeoPosition | null = {
                            latitude: 0,
                            longitude: 0,
                            accuracy: 0,
                            timestamp: 0
                        };

                        const result = await multiPhotoService.handleMultiPhotoSelectionArrayWithBatched(
                            photos,
                            (updateFn) => setPhotosArray(updateFn),
                            currentPosition,
                            'gallery' // 相册选择来源：相册
                        );
                        
                        if (!result.success) {
                            console.warn('部分图片上传失败:', result.errors);
                            // 可以显示提示信息
                        }
                    } catch (error) {
                        console.error('多图片处理失败:', error);
                    }
                }}
            />
        );
    })()

    return (
        <div className="flex flex-col h-screen">
            <div {...getNavBarContainerStyle()}>
                <NavBar onBack={() => {
                    if (isFromNativeMapMode) {
                        openNativeMapMode()
                        navigateBack.navigateToDeliveringList(navigate);
                    } else {
                        navigate(-1);
                    }
                }}>{trackingNumber}</NavBar>
            </div>

            <div className="flex-1 overflow-y-auto">
                {/* 订单信息 */}
                <div className="mb-1 bg-white rounded-lg p-4 shadow-sm">
                    <h2 className="text-base font-semibold mb-1">{t('pages.delivery.detail.orderInformation')}</h2>
                    <div className="mb-1">
                        <InfoItem label={`${t('pages.delivery.detail.trackingNumber')}`} value={trackingNumber}/>
                        <InfoItem label={`${t('pages.delivery.detail.stopNo')}`} value={deliveryTaskOrder ? formatStopNumberFromDeliveryTaskOrder(deliveryTaskOrder) : '0'}/>
                        <InfoItem label={`${t('pages.delivery.detail.routeNo')}`} value={routeNumber}/>
                        <InfoItem label={`${t('pages.delivery.detail.assignedTime')}`}
                                  value={deliverAt ? formatLocalizedDateTime(timestampToDate(deliverAt)) : ''}/>
                        <InfoItem label={`${t('pages.delivery.detail.deliveryAttempt')}`}
                                  value={`${deliveryAttempts} out of 3`}/>
                        <OrderNoteItem 
                            orderNote={deliveryTaskOrder?.orderNote}
                            currentLanguage={i18n.language}
                        />
                        {deliveredAt && (
                            <InfoItem label={`${t('pages.delivery.detail.deliveredAt')}`}
                                      value={formatLocalizedDateTime(timestampToDate(deliveredAt)) || ''}/>
                        )}
                    </div>
                    {/* 客户信息 */}
                    <h2 className="text-base font-semibold mb-1">{t('pages.delivery.detail.customerInformation')} </h2>
                    <div>
                        <InfoItem label={`${t('pages.delivery.detail.name')}`} value={fullName || ''} />
                        <InfoItem label={`${t('pages.delivery.detail.phoneNumber')}`} value={`${maskPhoneNumber(phone)}${extension ? `-${extension}` : ''}`} />
                        <InfoItem label={`${t('pages.delivery.detail.address')}`} value={address} />
                        {/* 妥投方式：仅当版本>8.2.0、已妥投、接口返回 deliveryMethod 且 store 有 i18n 配置时展示 */}
                        {isVersionGte820 && deliveryTaskOrder?.isDelivered && deliveryTaskOrder?.deliveryMethod && deliveryMethodI18nConfig.length > 0 && (() => {
                            const item = deliveryMethodI18nConfig.find(i => i.code === deliveryTaskOrder.deliveryMethod);
                            if (!item) return null;
                            return <DeliveryMethodDisplayCard item={item} />;
                        })()}
                    </div>
                </div>

                {/*pod审核不通过原因 - 只从driver-violation-review入口进入时显示（detailType === 'pod'） */}
                {(() => {
                    if (detailType !== 'pod') {
                        return null;
                    }
                    // 适配数据结构：将 PodReviewMessageItemType[] 转换为 string[]，与main分支逻辑一致
                    const podReviewMessageStrings = getTranslatedPodReviewMessage(podReviewMessage) || [];
                    return podReviewStatus === PodReviewStatus.MANUAL_DISAPPROVED && podReviewMessageStrings && podReviewMessageStrings.length > 0 && (
                        <div className="mb-1 bg-white rounded-lg p-4 shadow-sm">
                            <div className="flex flex-col text-sm">
                                {/* 固定提示文本 */}
                                <div className="text-red-700 font-semibold mb-2">
                                    {t('pages.delivery.podReview.manualDisapproveReason')}
                                </div>
                                {/* KNOWN_ERROR_TYPES 内的错误（国际化） */}
                                {(() => {
                                    const knownErrors = podReviewMessageStrings.filter(msg => KNOWN_ERROR_TYPES.includes(msg));
                                    if (knownErrors.length > 0) {
                                        return (
                                            <div className="text-black mb-2">
                                                {knownErrors.map(msg => 
                                                    t(`pages.delivery.podReview.errorTypes.${msg}`, { defaultValue: msg })
                                                ).join('; ')}
                                            </div>
                                        );
                                    }
                                    return null;
                                })()}
                                {/* 其他信息（单行，省略号，可点击展开） */}
                                {(() => {
                                    const otherMessages = podReviewMessageStrings.filter(msg => !KNOWN_ERROR_TYPES.includes(msg));
                                    if (otherMessages.length > 0) {
                                        return (
                                            <div
                                                className={`text-black ${!isOtherMessagesExpanded ? 'truncate' : ''} active:opacity-70`}
                                                onClick={() => setIsOtherMessagesExpanded(!isOtherMessagesExpanded)}
                                            >
                                                {otherMessages[0]}
                                            </div>
                                        );
                                    }
                                    return null;
                                })()}
                                {/* 调整提示 */}
                                <div className="text-red-700 mt-2">
                                    {t('pages.delivery.podReview.adjustmentNotice')}
                                </div>
                            </div>
                        </div>
                    );
                })()}
                {/*区分场景 展示不同的页面*/}
                {canEdit ? (
                    // canEdit === true: 显示可编辑的照片上传界面
                    <div className="mb-2 bg-white rounded-lg p-4 shadow-sm">
                        <h2 className="text-base font-semibold mb-1">{t('pages.delivery.detail.photos')}</h2>
                        <p className="text-sm text-gray-600 mb-1">
                            {showMultiPhotoSelector 
                                ? t('pages.delivery.detail.photoRequirement')
                                : t('pages.delivery.detail.photoDesc')
                            }
                        </p>

                        {!showMultiPhotoSelector && <div className="grid grid-cols-3 gap-2 my-4">
                            <PhotoUploadCard
                                photoUrl={photos.label?.url}
                                type="label"
                                description={`${t('pages.delivery.detail.parcelLabel')}`}
                                onTakePhoto={takePhoto}
                                onDelete={deletePhoto}
                                onView={handleViewPhoto}
                            />
                            <PhotoUploadCard
                                photoUrl={photos.location?.url}
                                type="location"
                                description={`${t('pages.delivery.detail.parcelLocation')}`}
                                onTakePhoto={takePhoto}
                                onDelete={deletePhoto}
                                onView={handleViewPhoto}
                            />
                            <PhotoUploadCard
                                photoUrl={photos.houseNumber?.url}
                                type="houseNumber"
                                description={`${t('pages.delivery.detail.houseNumber')}`}
                                onTakePhoto={takePhoto}
                                onDelete={deletePhoto}
                                onView={handleViewPhoto}
                            />
                        </div>}
                        {/* 多图画廊展示 >=1.2.0 */}
                        {showMultiPhotoSelector && <div className="mt-4">
                            <MultiPhotoGalleryView 
                                photos={photosArray
                                        .filter((photo): photo is PhotoType => photo?.url !== undefined)
                                        .map(photo => photo.url)
                                    }
                                    onDelete={(index: number) => {
                                        deletePhotoArray(index);
                                    }}
                                    maxDisplayCount={6}
                                    onNativeBurstCapture={handleMultiPhotoGalleryBurstCapture}
                                />
                        </div>}
                        {/* 相册上传 >=1.2.0 */}
                        {showMultiPhotoSelector && photosArray.length < kMaxSelectorPhotoLength && <div className="mt-4">
                            {multiPhotoSelectorView}
                        </div>}
                    </div>
                ) : (
                    // canEdit === false: 显示只读的照片展示界面
                    <div>
                        <div className="mb-2 bg-white rounded-lg p-4 shadow-sm">
                            <h2 className="text-base font-semibold mb-1">{t('pages.delivery.detail.photos')}</h2>
                            <div className="grid grid-cols-2 gap-4">
                                {deliveryTaskOrder?.orderWithAddress?.order?.podImageS3UrlsJson ? (
                                    deliveryTaskOrder?.orderWithAddress?.order?.podImageS3UrlsJson.map((photo: string, index: number) => (
                                        <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 w-full">
                                            <Image
                                                src={photo}
                                                alt={`Delivery photo ${index + 1}`}
                                                fit="cover"
                                                className="w-full h-full object-cover rounded-lg"
                                                onClick={() => setViewingPhoto(photo)}
                                            />
                                        </div>
                                    ))
                                ) : (
                                    <>
                                        <div className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 w-full flex items-center justify-center">
                                            <Image src="/404" className="w-full h-full object-cover rounded-lg"/>
                                        </div>
                                        <div className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 w-full flex items-center justify-center">
                                            <Image src="/404" className="w-full h-full object-cover rounded-lg"/>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* 警告提示 */}
                {warningMessage && (
                    // warningMessage 存在时显示警告信息
                    <p className="text-sm text-center text-red-600">{warningMessage}</p>
                )}

                {/* 查看大图 */}
                <PhotoViewer
                    photoUrl={viewingPhoto}
                    onClose={() => setViewingPhoto(null)}
                />
            </div>

            {/* 提交按钮 */}
            {showButton && (
                // showButton === true: 显示按钮
                <div {...getBottomNavBarContainerStyle('flex-0 flex px-4 py-2 mt-auto gap-4')}>
                    <Button
                        block
                        color="primary"
                        loading={isLoading}
                        disabled={buttonDisabled}  // buttonDisabled === true 时按钮灰色不可点击
                        onClick={handleCompleteDelivery}
                    >
                        {t('pages.delivery.detail.complete')}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default DeliveredDetailPage;
